import type { ScriptStatus } from './types';

// Deadline framing for the review page — always framed around keeping the
// project schedule on track, never tied to a shoot date (Henry's Phase 0
// review). Plain dates in, plain dates out; no instants, so nothing shifts a
// day across timezones.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format a plain ISO date (YYYY-MM-DD) as e.g. "Fri 10 Jul". Built in UTC and
 * assembled by hand (no `toLocaleDateString`) so the output is deterministic —
 * stable across timezones and independent of the runtime's ICU locale data.
 * Returns the input unchanged if it isn't a valid calendar date.
 */
export function formatPlainDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return iso;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return iso;
  const d = new Date(Date.UTC(year, month - 1, day));
  // Reject impossible dates (e.g. 2026-02-31 rolls over to March).
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return iso;
  }
  return `${WEEKDAYS[d.getUTCDay()]} ${day} ${MONTHS[month - 1]}`;
}

export type DeadlineLine =
  | { kind: 'due'; verb: string; date: string; text: string }
  | { kind: 'overdue'; text: string }
  | null;

/**
 * The one-line deadline shown under the progress tracker. Returns null when
 * there's nothing to show (already approved, or no approve_by set).
 *
 * `todayIso` is the caller's current plain date (YYYY-MM-DD). Overdue is a
 * lexicographic string compare, which is chronological for ISO dates and needs
 * no timezone/DST arithmetic.
 */
export function deadlineLine(
  status: ScriptStatus,
  approveBy: string | null | undefined,
  todayIso: string,
): DeadlineLine {
  if (status === 'approved') return null;
  const iso = (approveBy ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;

  if (iso < todayIso) {
    return {
      kind: 'overdue',
      text: 'This one’s past its target date. Send it back when you can so we can keep the schedule on track.',
    };
  }

  // Before the first revision round the client can't approve yet, so the ask is
  // to send feedback; from v2 on, the ask is to approve.
  const verb = status === 'v2' || status === 'v3' ? 'Approve' : 'Send your feedback';
  const date = formatPlainDate(iso);
  return {
    kind: 'due',
    verb,
    date,
    text: `${verb} by ${date} to keep the project schedule on track.`,
  };
}
